import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  CustomForm,
  CustomFormItem,
  CustomNumberInput,
  CustomSpinSelect,
  OptionSpin,
  Text,
  CustomCheckbox,
  useText,
} from '@src/components';
import { Form, Row, Col, Checkbox, Button } from 'antd';
import { UpCircleOutlined, DownCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  getVehicleLimitTypes,
  getVehicleLimitValueTypes,
} from '@src/store/slice/addNewCarStore/addCarPlateDetailSlice';
import '@src/styles/addNewCar/addCarSetLimitType.scss';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import { setLoadingState } from '@src/store/slice/appSettingsSlice';
import { useDaysOfWeek } from '@src/hooks/useDaysOfWeek';

const AddCarSetLimitType = ({
  index,
  limitDetails,
  setLimitDetails,
  plateFields,
  copyLimitDetailsChecked,
  setCopyLimitDetailsChecked,
  limitDetailsState,
  openExpandLimitDetails = false,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { vehicleLimitTypes, vehicleLimitValueTypes } = useSelector(
    (state) => state.addCarPlateDetail,
  );
  const { selectVehicleProviderType } = useSelector((state) => state.addCarType);
  const [isLimitEnabled, setIsLimitEnabled] = useState(false);
  const [checkAll, setCheckAll] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const isMobil = useMediaQuery({ query: '(max-width: 768px)' });
  const { selectedCustomer } = useSelector((state) => state?.customer);
  const { isAdmin } = useSelector((state) => state.auth);
  const { selectedUserId } = useSelector((state) => state.user);

  const limitType = useText('limitType');
  const limitValueType = useText('limitValueType');
  const limitValue = useText('limitValue');
  // The selected days are kept as the (localized) labels themselves - both in this component's
  // state and in the wizard's limitDetails - so they have to be moved over to the new labels when
  // the language is switched. Otherwise the day buttons look unselected and the next toggle
  // rebuilds the usable-days mask without the days that were already picked.
  const daysOfWeek = useDaysOfWeek((remapDays) => {
    setSelectedDays((current) => remapDays(current));
    if (limitDetails?.selectedDays) {
      setLimitDetails({
        ...limitDetails,
        selectedDays: remapDays(limitDetails.selectedDays),
      });
    }
  });

  const fetchVehicleLimitValueTypes = async () => {
    await dispatch(setLoadingState(true));
    await dispatch(
      getVehicleLimitValueTypes({
        custId: isAdmin && selectedUserId ? selectedUserId : selectedCustomer.customerId,
      }),
    );
    await dispatch(setLoadingState(false));
  };

  const fetchVehicleLimitTypes = async () => {
    await dispatch(setLoadingState(true));
    await dispatch(
      getVehicleLimitTypes({
        custId: isAdmin && selectedUserId ? selectedUserId : selectedCustomer.customerId,
        vouId: selectVehicleProviderType,
      }),
    );
    await dispatch(setLoadingState(false));
  };

  useEffect(() => {
    if (vehicleLimitTypes?.length < 1) {
      fetchVehicleLimitTypes();
    }
    if (vehicleLimitValueTypes?.length < 1) {
      fetchVehicleLimitValueTypes();
    }
  }, [vehicleLimitTypes.length, vehicleLimitValueTypes.length, dispatch]);

  useEffect(() => {
    if (limitDetails) {
      setIsLimitEnabled(limitDetails.isLimitEnabled || limitDetails.limitValue || false);
      setCheckAll(limitDetails.checkAll || false);
      setSelectedDays(limitDetails.selectedDays || []);
      form.setFieldsValue(limitDetails);
    }
  }, [limitDetails, form]);

  useEffect(() => {
    setIsLimitEnabled(openExpandLimitDetails);
    setIsExpanded(openExpandLimitDetails);
  }, [openExpandLimitDetails]);

  const isPreviousFormValid = () => {
    if (index === 0) return false;
    const previousLimitDetails = limitDetailsState[index - 1];
    if (!previousLimitDetails) return false;
    const requiredFields = ['limitTypeId', 'limitValueTypeId', 'limitValue'];
    return requiredFields.every((field) => previousLimitDetails[field]);
  };

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setIsLimitEnabled(checked);
    setLimitDetails({ ...limitDetails, isLimitEnabled: checked });
    if (!checked) {
      setIsExpanded(false);
      setCheckAll(false);
      //setSelectedDays([]);
      //form.resetFields();
      //setLimitDetails({});
    } else {
      setIsExpanded(true);
    }
  };

  const generateFuelDaysString = (selectedDays) => {
    return daysOfWeek.map((day) => (selectedDays.includes(day) ? '1' : '0')).join('');
  };

  const handleCheckAllChange = (e) => {
    const checked = e.target.checked;
    const selectedDays = checked ? daysOfWeek : [];

    const fuelDaysString = generateFuelDaysString(selectedDays);

    setCheckAll(checked);
    setSelectedDays(selectedDays);
    setLimitDetails({
      ...limitDetails,
      checkAll: checked,
      selectedDays: selectedDays,
      fuelDays: fuelDaysString,
    });
  };

  const handleDayChange = (day) => {
    const updatedDays = selectedDays.includes(day)
      ? selectedDays.filter((d) => d !== day)
      : [...selectedDays, day];

    const fuelDaysString = generateFuelDaysString(updatedDays);

    setSelectedDays(updatedDays);
    setCheckAll(updatedDays.length === 7);
    setLimitDetails({
      ...limitDetails,
      selectedDays: updatedDays,
      fuelDays: fuelDaysString,
      checkAll: updatedDays.length === 7,
    });
  };

  const handleCopyLimitDetailsChange = (e) => {
    const checked = e.target.checked;
    const previousIndex = index - 1;

    setCopyLimitDetailsChecked((prevChecked) => ({
      ...prevChecked,
      [index]: checked,
    }));

    if (checked && previousIndex >= 0 && plateFields[previousIndex]) {
      const previousLimitDetails = limitDetailsState[previousIndex] || {};
      setLimitDetails({ ...previousLimitDetails, copyLimitDetails: checked });
      form.setFieldsValue({ ...previousLimitDetails, copyLimitDetails: checked });
      setIsLimitEnabled(previousLimitDetails.isLimitEnabled || false);
      setCheckAll(previousLimitDetails.checkAll || false);
      setSelectedDays(previousLimitDetails.selectedDays || []);
    } else {
      setLimitDetails({});
      form.resetFields();
      setIsLimitEnabled(false);
      setCheckAll(false);
      setSelectedDays([]);
    }
  };

  const handleValuesChange = (changedValues, allValues) => {
    Object.keys(changedValues).forEach((key) => {
      if (key === 'limitTypeId') {
        const selectedLimitType = vehicleLimitTypes.find(
          (type) => type.limitTypeId === changedValues[key],
        );
        allValues.limitType = {
          id: selectedLimitType?.limitTypeId,
          name: selectedLimitType?.limitTypeLabel,
        };
      } else if (key === 'limitValueTypeId') {
        const selectedValueType = vehicleLimitValueTypes.find(
          (type) => type.id === changedValues[key],
        );
        allValues.limitValueType = {
          id: selectedValueType?.id,
          name: selectedValueType?.limitValueTypeLabel,
        };
      }
    });

    const newPlateForm = { ...limitDetails, ...allValues };
    let newIsLimitEnabled = false;
    if (
      newPlateForm?.limitTypeId !== limitDetailsState[index - 1]?.limitTypeId ||
      newPlateForm?.limitValueTypeId !== limitDetailsState[index - 1]?.limitValueTypeId
    ) {
      newIsLimitEnabled = false;
    } else {
      newIsLimitEnabled = true;
    }
    if (newIsLimitEnabled !== isLimitEnabled) {
      //setIsLimitEnabled(newIsLimitEnabled);
    }
    form.setFields([{ name: 'limitValue', errors: [] }]);
    setLimitDetails({ ...limitDetails, ...allValues });
  };

  const handleToggleExpand = () => {
    const newIsExpanded = !isExpanded;

    setIsLimitEnabled(newIsExpanded);
    setLimitDetails({ ...limitDetails, isLimitEnabled: newIsExpanded });
    if (!newIsExpanded) {
      setIsExpanded(false);
      setCheckAll(false);
      //setSelectedDays([]);
      //form.resetFields();
    } else {
      setIsExpanded(true);
    }
  };

  const validateLimitValue = (_, value) => {
    const limitTypeId = form.getFieldValue('limitTypeId');
    const limitValueTypeId = form.getFieldValue('limitValueTypeId');

    if (!limitTypeId || !limitValueTypeId) {
      return Promise.reject(new Error('Lütfen önce Limit Tipi ve Limit Değer Tipi seçin.'));
    }

    return Promise.resolve();
  };

  return (
    <div className="add-car-set-limit-type">
      <Row justify={'space-between'} align="middle" className="header-row-title-2">
        <Checkbox
          className="custom-checkbox-wrapper"
          onChange={handleCheckboxChange}
          checked={isLimitEnabled}
        >
          <Text t="iWantToSetALimit" />
        </Checkbox>
        <Button
          type="text"
          icon={
            isExpanded ? (
              <UpCircleOutlined style={{ color: '#1d2228', fontSize: '24px' }} />
            ) : (
              <DownCircleOutlined style={{ color: '#1d2228', fontSize: '24px' }} />
            )
          }
          onClick={handleToggleExpand}
          className="toggle-button"
        />
      </Row>
      {isLimitEnabled && isExpanded && (
        <>
          <CustomForm
            name={`limitDetails-${index}`}
            className="filter-form"
            autoComplete="off"
            initialValues={{}}
            layout={'vertical'}
            form={form}
            onValuesChange={handleValuesChange}
          >
            {index > 0 && (
              <div className="form-item">
                <CustomFormItem label={false} name="copyLimitDetails" valuePropName="checked">
                  <CustomCheckbox
                    disabled={!isPreviousFormValid()}
                    checked={copyLimitDetailsChecked[index]}
                    onChange={handleCopyLimitDetailsChange}
                  >
                    <span className="address-form-item">
                      <Text t="applyTheSelectionsOfTheVehicleAboveTo" />
                    </span>
                  </CustomCheckbox>
                </CustomFormItem>
              </div>
            )}

            <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} className="form-item">
              <Col span={isMobil ? 24 : 8}>
                <CustomFormItem
                  label={<Text t="limitType" />}
                  name="limitTypeId"
                  className={'delivery-address-county-form-item'}
                >
                  <CustomSpinSelect
                    id="add-car-set-limit-type-limitTypeId-select"
                    placeholder={limitType}
                    disabled={vehicleLimitTypes.length === 0}
                    loading={vehicleLimitTypes.length === 0}
                    height={36}
                  >
                    {vehicleLimitTypes?.map((vehicle) => (
                      <OptionSpin
                        id={`add-car-set-limit-type-limitTypeId-${vehicle?.limitTypeLabel}-option`}
                        key={vehicle?.limitTypeId}
                        value={vehicle?.limitTypeId}
                      >
                        {vehicle?.limitTypeLabel}
                      </OptionSpin>
                    ))}
                  </CustomSpinSelect>
                </CustomFormItem>
              </Col>

              <Col span={isMobil ? 24 : 8}>
                <CustomFormItem
                  label={<Text t="limitValueType" />}
                  name="limitValueTypeId"
                  className={'delivery-address-county-form-item'}
                >
                  <CustomSpinSelect
                    id="add-car-set-limit-type-limitValueTypeId-select"
                    placeholder={limitValueType}
                    disabled={!form.getFieldValue('limitTypeId')}
                    loading={vehicleLimitValueTypes.length === 0}
                    height={36}
                  >
                    {vehicleLimitValueTypes?.map((vehicle) => (
                      <OptionSpin
                        id={`add-car-set-limit-type-limitValueTypeId-${vehicle?.limitValueTypeLabel}-option`}
                        key={vehicle?.id}
                        value={vehicle?.id}
                      >
                        {vehicle?.limitValueTypeLabel}
                      </OptionSpin>
                    ))}
                  </CustomSpinSelect>
                </CustomFormItem>
              </Col>

              <Col span={isMobil ? 24 : 8}>
                <CustomFormItem
                  label={<Text t="limitValue" />}
                  name="limitValue"
                  className={'delivery-address-county-form-item'}
                  rules={[
                    {
                      validator: validateLimitValue,
                    },
                  ]}
                >
                  <CustomNumberInput
                    id="add-limit-value-numberInput"
                    autoComplete="off"
                    placeholder={limitValue}
                    disabled={!form.getFieldValue('limitValueTypeId')}
                    height={36}
                  />
                </CustomFormItem>
              </Col>
            </Row>
          </CustomForm>
          <div className="responsive-container">
            <div className="top-row">
              <div className="question">
                <Text t="whichDaysCanThisVehiclePurchaseFuel" />
              </div>
              <div className="checkbox-container">
                <CustomCheckbox checked={checkAll} onChange={handleCheckAllChange}>
                  <span className="address-form-item">
                    <Text t="allDays" />
                  </span>
                </CustomCheckbox>
              </div>
            </div>
            <div className="input-container">
              {daysOfWeek.map(
                (day) => (
                  <Button
                    key={day}
                    type={selectedDays.includes(day) ? 'primary' : 'default'}
                    onClick={() => handleDayChange(day)}
                  >
                    {day}
                  </Button>
                ),
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

AddCarSetLimitType.propTypes = {
  index: PropTypes.number.isRequired,
  limitDetails: PropTypes.object.isRequired,
  setLimitDetails: PropTypes.func.isRequired,
  plateFields: PropTypes.array.isRequired,
  copyLimitDetailsChecked: PropTypes.object.isRequired,
  setCopyLimitDetailsChecked: PropTypes.func.isRequired,
  limitDetailsState: PropTypes.array.isRequired,
  openExpandLimitDetails: PropTypes.bool,
};

export default AddCarSetLimitType;
